import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import CarrierAutocomplete from "../../../../../Inputs/CarrierAutocomplete/CarrierAutocomplete";
import PrimaryTextField from "../../../../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimarySelect from "../../../../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryButton from "../../../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../../../Buttons/SecondaryButton/SecondaryButton";
import WarningMessage from "../../../../../WarningMessage/WarningMessage";
import { handleAltEnter } from "../../../../../../utils/keyboardShortcutHelpers";
import { formatCurrency, formatTitleCase } from ".././../../../../../utils/formatHelpers";

export default function AccessorialsStepOne({
  activeUser,
  activeLoad,
  accessorialTypes,
  stagedAccessorialsArray,
  setStagedAccessorialsArray,
  accessorialToEdit,
  setStep,
  addNew,
  handleCancel
}) {
  const [selectedStopId, setSelectedStopId] = useState("");
  const [selectedStop, setSelectedStop] = useState({});
  const [stops, setStops] = useState([]);
  const [selectedAccessorialTypeId, setSelectedAccessorialTypeId] = useState("");
  const [selectedAccessorialType, setSelectedAccessorialType] = useState({});
  const [quantity, setQuantity] = useState("1");
  const [carrierCost, setCarrierCost] = useState("");
  const [carrierCostTotal, setCarrierCostTotal] = useState("");
  const [carrier, setCarrier] = useState({});
  const [carrierId, setCarrierId] = useState("");
  const [selectedRateType, setSelectedRateType] = useState("Flat Rate");
  const [description, setDescription] = useState("");
  const [reviewDisabled, setReviewDisabled] = useState(true);
  const [initialValues, setInitialValues] = useState(null);
  const [warningText, setWarningText] = useState("");
  const settlementError = "Extra pay cannot be added after the movement has been transferred to settlement. \nContact cfm-payables@capstonelogistics.com for further assistance.";

  const rateTypes = ["Flat Rate", "Per Mile"];

  const handleReview = () => {
    const accessorialToStage = {
      totalAmount: parseFloat(carrierCostTotal, 2),
      amount: parseFloat(carrierCost, 2),
      quantity: quantity,
      currency: "USD",
      deductCodeId: selectedAccessorialTypeId,
      movementId: activeLoad.movements[0].id,
      orderId: activeLoad.id,
      enteredUserId: activeUser.userIdMcld,
      description: description,
      shortDescription: description,
      accType: selectedAccessorialType.shortDescription,
      tempId: accessorialToEdit ? accessorialToEdit.tempId : null,
      stopSequence: selectedStop?.orderSequence,
      stopId: selectedStop?.id,
      rateType: selectedRateType,
      carrierId: carrier.id,
      submitted: false,
      accessorialId: initialValues?.id || accessorialToEdit?.accessorialId
    };

    const acccessorialsArray = [...stagedAccessorialsArray];
    const index = acccessorialsArray.findIndex(c => c.tempId === accessorialToEdit.tempId);

    if (index !== -1) {
      acccessorialsArray.splice(index, 1, accessorialToStage);
    } else {
      if (stagedAccessorialsArray.length) {
        accessorialToStage.tempId = Math.max(...stagedAccessorialsArray.map(o => o.tempId)) + 1;
        acccessorialsArray.push(accessorialToStage);
      } else {
        accessorialToStage.tempId = 1;
        acccessorialsArray.push(accessorialToStage);
      }
    }

    setStagedAccessorialsArray(acccessorialsArray);
    setStep(2);
  };

  useEffect(() => {
    if (activeLoad?.stops) {
      let parsedStops = [];

      parsedStops = activeLoad.stops.map((stop => (
        {
          label: `Stop ${stop.orderSequence}: ${formatTitleCase(stop.cityName)}, ${stop.state} (${stop.stopType})`,
          id: stop.id,
          ...stop
        }
      )));

      setStops(parsedStops);

      if (parsedStops.length) {
        setSelectedStopId(parsedStops[0].id);
      }
    }

    if (activeLoad.payeeId && !accessorialToEdit.tempId && !initialValues) {
      setCarrierId(activeLoad.payeeId);
    }
  }, [activeLoad, initialValues]);

  useEffect(() => {
    if (selectedAccessorialType && !accessorialToEdit.tempId && !initialValues?.description) {
      setDescription(selectedAccessorialType.shortDescription);
    }
  }, [selectedAccessorialType]);

  useEffect(() => {
    setSelectedAccessorialType(accessorialTypes.find(type => type.id === selectedAccessorialTypeId));
  }, [selectedAccessorialTypeId]);

  useEffect(() => {
    setSelectedStop(stops.find(stop => stop.id === selectedStopId));
  }, [selectedStopId]);

  useEffect(() => {
    if (selectedRateType === "Flat Rate") {
      setCarrierCostTotal(carrierCost * quantity);
    } else if (selectedRateType === "Per Mile") {
      setCarrierCostTotal(quantity * carrierCost * activeLoad.miles);
    }
  }, [selectedRateType, quantity, carrierCost]);

  useEffect(() => {
    if (accessorialToEdit?.tempId && accessorialTypes.length) {
      setSelectedStopId(accessorialToEdit.stopId);
      setSelectedAccessorialTypeId(accessorialToEdit.deductCodeId);
      setSelectedRateType(accessorialToEdit.rateType ?? "Flat Rate");
      setQuantity(accessorialToEdit.quantity);
      setCarrierCost(accessorialToEdit.amount);
      setDescription(accessorialToEdit.description);
      setCarrierId(accessorialToEdit.carrierId);
    }
  }, [accessorialToEdit, accessorialTypes]);

  useEffect(() => {
    if (
      !selectedRateType ||
      !selectedAccessorialTypeId ||
      !carrierCost ||
      parseFloat(carrierCost) <= 0 ||
      isNaN(parseFloat(carrierCost)) ||
      !quantity ||
      parseInt(quantity) <= 0 ||
      !carrier ||
      (activeLoad?.settlementDate && ![1, 2, 8].includes(activeUser?.roleId))
    ) {
      setReviewDisabled(true);
    } else {
      setReviewDisabled(false);
    }

    if (selectedAccessorialTypeId === "TON") {
      setWarningText("Adding this accessorial will automatically clear carrier linehaul and fuel");
    } else if (activeLoad?.settlementDate && ![1, 2, 8].includes(activeUser?.roleId)) {
      // Hint 1 = Super Admin, 2 = Admin, 8 = Accounts Payable
      setWarningText(settlementError);
    } else {
      setWarningText("");
    }
  }, [
    selectedRateType,
    selectedAccessorialType,
    carrierCost,
    quantity,
    carrier,
    activeLoad,
    activeUser
  ]);

  useEffect(() => {
    if (initialValues && accessorialTypes?.length && !accessorialToEdit) {
      setSelectedStopId(initialValues.stopId ? initialValues.stopId : "");
      setSelectedAccessorialTypeId(initialValues.deductCodeId);
      setCarrierId(initialValues.carrierId);
      setQuantity(initialValues.quantity);
      setCarrierCost(initialValues.rate);
      setDescription(initialValues.description);
    }
  }, [initialValues, accessorialTypes, accessorialToEdit]);

  useEffect(() => {
    if (!accessorialToEdit && !addNew) {
      setInitialValues(accessorialToEdit);
    } else {
      setInitialValues(null);
    }
  }, [accessorialToEdit, addNew]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      handleAltEnter(e, reviewDisabled, handleReview);
    };

    document.addEventListener("keydown", handleKeyDown);

    return function cleanup() {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    reviewDisabled,
    carrierCostTotal,
    selectedStop,
    selectedAccessorialType,
    quantity,
    carrier,
    description,
    selectedRateType,
    selectedAccessorialTypeId
  ]);

  return (
    <Box>
      <Box className="Accessorial-modal-form-row" sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="subhead2" color="black_solid.80">
          New Accessorial for
        </Typography>
        <Box sx={{ margin: "10px 0 0 10px" }}>
          <PrimarySelect
            id="stop-select"
            options={stops}
            onChange={setSelectedStopId}
            variant="standard"
            value={selectedStopId}
            nameKey="label"
            valueKey="id"
            width="240px"
          />
        </Box>
      </Box>
      <Box className="Accessorial-modal-form-row" sx={{ display: "flex" }}>
        <Box>
          <PrimarySelect
            id="accessorial-type-select"
            label="Accessorial"
            options={accessorialTypes}
            onChange={setSelectedAccessorialTypeId}
            value={selectedAccessorialTypeId}
            nameKey="shortDescription"
            valueKey="id"
            width="380px"
            optionsHeight="300px"
            error={!selectedAccessorialTypeId}
          />
        </Box>
        <Box sx={{ paddingLeft: "50px" }}>
          <CarrierAutocomplete
            title="Verify the Carrier"
            titleSize="body1.fontSize"
            width="380px"
            carrierId={carrierId}
            selectedOption={carrier}
            updateSelectedCarrier={setCarrier}
            error={!carrier}
          />
        </Box>
      </Box>
      <Box className="Accessorial-modal-form-row">
        {selectedAccessorialType &&
          <PrimaryTextField
            id="accessorial-step-one-description"
            label="Description"
            placeholder="Describe the charge"
            value={description}
            onChange={setDescription}
            width="810px"
            maxLength="500"
          />
        }
      </Box>
      <Box className="Accessorial-modal-form-row" sx={{ display: "flex" }}>
        <Box>
          <PrimarySelect
            id="accessorial-step-one-rate-type-select"
            label="Rate"
            options={rateTypes}
            onChange={setSelectedRateType}
            value={selectedRateType}
            width="180px"
            optionsHeight="300px"
            error={!selectedRateType}
          />
        </Box>
        <Box sx={{ marginLeft: "30px" }}>
          <PrimaryTextField
            id="accessorial-step-one-quantity"
            label="Quantity"
            value={quantity}
            onChange={setQuantity}
            width="70px"
            isInteger
            enableIncrement
            error={quantity <= 0}
          />
        </Box>
        <Box sx={{ marginLeft: "30px" }}>
          <PrimaryTextField
            id="accessorial-step-one-carrier-cost"
            label="Carrier Cost"
            value={carrierCost}
            onChange={setCarrierCost}
            width="100px"
            isCurrency
            error={carrierCost <= 0}
          />
        </Box>
        <Box sx={{ margin: "3px 0 0 50px" }}>
          <Typography variant="subhead1" color="black_solid.80" sx={{ marginTop: "7px" }}>
            Total
          </Typography>
          <Typography variant="subhead1" color="black_solid.80" sx={{ margin: "13px 0 0 4px" }}>
            {formatCurrency(carrierCostTotal, 2)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", marginTop: "50px" }}>
        <Box>
          {warningText &&
            <WarningMessage text={warningText} displayText={warningText} />
          }
        </Box>
        <Box sx={{ margin: "0 20px 0 auto" }}>
          <SecondaryButton
            label="Cancel"
            onClick={handleCancel}
          />
        </Box>
        <Box>
          <PrimaryButton
            label="Review"
            onClick={handleReview}
            disabled={reviewDisabled}
          />
        </Box>
      </Box>
    </Box>
  );
}

AccessorialsStepOne.propTypes = {
  activeUser: PropTypes.object,
  activeLoad: PropTypes.object.isRequired,
  accessorialTypes: PropTypes.array.isRequired,
  stagedAccessorialsArray: PropTypes.array.isRequired,
  setStagedAccessorialsArray: PropTypes.func.isRequired,
  accessorialToEdit: PropTypes.object,
  setStep: PropTypes.func.isRequired,
  addNew: PropTypes.bool,
  handleCancel: PropTypes.func.isRequired
};